import { Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ISrsBallotVote, SrsVoteId, srsVoteOption } from '../../shared/models/sr/srs-vote';
import { ISrsBallotDetail } from '../../shared/models/sr/srs-ballot-details';
import { SrsBallotService } from '../../shared/services/srs-ballot.service';
import { Subscription } from 'rxjs';
import { ISubmitSrsBallotVotesRequest } from '../../shared/models/sr/submit-srs-ballot-votes-request';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { IApiResponse } from '../../../../shared/models/api-response';
import { IUser } from '../../../../shared/models/user';
import { ToastService } from '../../../../shared/services/toast.service';
import { IFileUpload } from '../../../controls/files-upload/files-upload.component';

@Component({
    selector: 'app-srs-place-vote',
    templateUrl: './srs-place-vote.component.html',
    styleUrl: './srs-place-vote.component.scss',
    standalone: false
})


export class SrsPlaceVoteComponent {

  @Input() ballot!: ISrsBallotDetail;
  @Input() user!: IUser;

  subscriptions: Subscription = new Subscription();

  voteOptionForm = new FormGroup({
    reconfirmSelectCtrl: new FormControl(false),
    reviseSelectCtrl: new FormControl(false),
    withdrawSelectCtrl: new FormControl(false),
    reconfirmCurrentPublicationConfirmationCtrl: new FormControl(false),
    leadProjectProposalCtrl: new FormControl(''),
    reconfirmNoteCtrl: new FormControl(''),
    reviseNoteCtrl: new FormControl(''),
    withdrawNoteCtrl: new FormControl(''),
    supportingInformationCtrl: new FormControl(null),
  });

  isFormValid: boolean = false;

  vote: ISrsBallotVote | undefined;

  noteTitle = "Please enter a reason for your decision";

  supportingInformationUploadProgress: number[] = [0];

  withdraw = srsVoteOption["SVT_WITHDRAW"];
  reconfirm = srsVoteOption["SVT_RECONFIRM"];
  revise = srsVoteOption["SVT_REVISE"];

  constructor(private activeModal: NgbActiveModal,
              private _srsBallotService: SrsBallotService,
              private _toastService: ToastService)
              {}

  get reconfirmSelect(){
    return this.voteOptionForm.get('reconfirmSelectCtrl');
  }
  get reviseSelect() {
    return this.voteOptionForm.get('reviseSelectCtrl');
  }
  get withdrawSelect() {
    return this.voteOptionForm.get('withdrawSelectCtrl');
  }
  get leadProjectProposal() {
    return this.voteOptionForm.get('leadProjectProposalCtrl');
  }
  get reconfirmCurrentPublicationConfirmation() {
    return this.voteOptionForm.get('reconfirmCurrentPublicationConfirmationCtrl');
  }
  get reconfirmNote() {
    return this.voteOptionForm.get('reconfirmNoteCtrl');
  }
  get reviseNote() {
    return this.voteOptionForm.get('reviseNoteCtrl');
  }
  get withdrawNote() {
    return this.voteOptionForm.get('withdrawNoteCtrl');
  }
  get supportingInformation() : FormControl {
    return this.voteOptionForm.get('supportingInformationCtrl') as FormControl;
  }

  ngOnInit(){
    this.loadExistingVote();

    this.voteOptionForm.valueChanges.subscribe(values => {
      this.validate();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadExistingVote() {

    this.vote = this.ballot?.votes?.find(vote => vote.email == this.user.email && vote.voteTypeId != SrsVoteId.SVT_NONE);

    if (!this.vote) return;

    const latestComment = this.vote?.comments ? this.vote?.comments[0] : null;

    if (this.vote.voteTypeId == SrsVoteId.SVT_RECONFIRM) {
      this.reconfirmSelect?.setValue(true);
      this.reconfirmNote?.setValue(latestComment?.comment ?? null);
      this.reconfirmCurrentPublicationConfirmation?.setValue(this.vote?.voteTypeId == SrsVoteId.SVT_RECONFIRM);
    }

    if (this.vote.voteTypeId == SrsVoteId.SVT_REVISE) {
      this.reviseSelect?.setValue(true);
      this.reviseNote?.setValue(latestComment?.comment ?? null);
      this.leadProjectProposal?.setValue(latestComment ?  latestComment.leadProjectProposal === true ? 'yes' : 'no' : '');
    }

    if (this.vote.voteTypeId == SrsVoteId.SVT_WITHDRAW) {
      this.withdrawSelect?.setValue(true);
      this.withdrawNote?.setValue(latestComment?.comment ?? null);
    }

  }

  selectReconfirm() {
    this.reset();
    this.reconfirmSelect?.setValue(true);
  }

  selectRevise() {
    this.reset();
    this.reviseSelect?.setValue(true);
  }

  selectWithdraw() {
    this.reset();
    this.withdrawSelect?.setValue(true);
  }

  reset() {
    this.reconfirmSelect?.setValue(false);
    this.reviseSelect?.setValue(false);
    this.withdrawSelect?.setValue(false);
  }

  cancel() {
    this.activeModal.dismiss('cancel');
  }

  close(voteTypeId: SrsVoteId) {
    this.activeModal.close(voteTypeId);
  }

  placeVote() {

    let vote: ISubmitSrsBallotVotesRequest = this.getUserVote();

    this.subscriptions.add(this._srsBallotService.placeSRSVote(vote).subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              this.supportingInformationUploadProgress[0] = Math.round(
                (100 * event.loaded) / event.total
              );
            }
            break;
          case HttpEventType.Response:
            this.supportingInformationUploadProgress[0] = 100;
            const apiResponse : IApiResponse<any> = event.body;
            if (apiResponse && apiResponse.success) {
              this._toastService.showSuccess("Your vote has been submitted. Thank you!", "Vote Successful");
              this.close(vote.voteTypeId);
            } else {
              this._toastService.showError("Your vote was not placed due to an unexpected error. Please try again or contact IT Support if issue persists.", "Vote Failed");
              this.close(vote.voteTypeId);
            }

            break;
        }
      },
      error: (error) => {
        this._toastService.showError("Your vote was not placed due to an unexpected error. Please try again or contact IT Support if issue persists.", "Vote Failed");
      }
    }));

  }

  getUserVote() : ISubmitSrsBallotVotesRequest {
    let currentVote = this.ballot.votes.find(vote => vote.email == this.user.email);
    let file: File | null = null;

    if (this.supportingInformation?.value?.files?.length > 0) {
      file = this.supportingInformation.value.files[0];
    }

    return  {
      ballotId: this.ballot?.ballotId,
      voteIds: [currentVote?.id??''],
      voteTypeId : this.reconfirmSelect?.value ? SrsVoteId.SVT_RECONFIRM : this.reviseSelect?.value ? SrsVoteId.SVT_REVISE : this.withdrawSelect?.value ? SrsVoteId.SVT_WITHDRAW : SrsVoteId.SVT_NONE,
      comment: this.reconfirmSelect?.value ? this.reconfirmNote?.value??'' : this.reviseSelect?.value ? this.reviseNote?.value??'' : this.withdrawSelect?.value ? this.withdrawNote?.value??'' : '',
      currentPublicationConfirmation: this.reconfirmCurrentPublicationConfirmation?.value??false,
      leadProjectProposal: this.leadProjectProposal?.value == "yes",
      supportingInformationDoc: file
    }
  }

  validate() {
    const supportingInformationUpload: IFileUpload = (this.supportingInformation as FormControl).value;
    const validSupportingDoc =
      (supportingInformationUpload?.files === null)
      || (supportingInformationUpload?.files === undefined)
      || (supportingInformationUpload?.files?.length === 0)
      || (!supportingInformationUpload?.hasError && supportingInformationUpload?.files.length > 0);

    this.isFormValid =
      (((this.reviseSelect?.value && this.leadProjectProposal?.value != "") ||
        (this.reconfirmSelect?.value && this.reconfirmCurrentPublicationConfirmation?.value) ||
        (this.withdrawSelect?.value)
      )??false)
        && validSupportingDoc;
  }
}
